import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useNavigation, useRevalidator } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// ShowOT — the same component used in Work Orders and Campaigns
import { ShowOT } from '../../../../../components/display/ShowOT';
import { OrderDataTypes } from '../../../../../components/display/ShowOT/types';
import { Loader } from '../../../../../components/inputs/Loader';
import { OrderType } from '../../../../../types/orders';

// Data selector — same as WorkOrderDetailed loader
import { selectWorkOrderWithChildren } from '../../../../../gql/selectors/orders';

// P15 Fase 1 (2026-07-08) — cross-link tram↔actuació. Ver
// docs/superpowers/specs/2026-07-08-p15-vincular-tram-wiring-design.md.
import { openModal } from '../../../../../state/actions/ui/modal';
import { modalIds } from '../../../../../components/modals/SuperModal/types';
import { useSurveillanceRoutes } from '../../../../../hooks/useSurveillanceRoutes';
import { useSegmentAvisoLink } from '../../../../../hooks/useSegmentAvisoLink';

// Corregir tramo (2026-08-05) — segundo punto de entrada al modal.
import { useAuth } from '../../../../../hooks/useAuth';
import { client } from '../../../../../gql/client';
import { GET_ROUTE_SEGMENTS } from '../../../../../gql/queries/surveillanceRoute';
import { EditSegmentConfirmPayload } from '../../../../../components/modals/submodals/EditSegmentModal/types';
import {
  buildActionSummaryPath,
  canChangeSegmentCauseInRoute,
  type SegmentEditorMode,
} from '../../../../../utils/surveillanceRoute';
import { hasSyntheticId } from '../../../../../offline/pendingSegmentsFromQueue';
import { preferServerWithCacheFallback } from '../../../../../gql/cacheFallback';
import { showErrorToast } from '../../../../../utils/toasts';
import { fetchExternalWorksBySegment } from '../../../../../gql/surveillanceRouteExternalWorks';
import { useOpenLinkedWork } from '../../../../../hooks/useOpenLinkedWork';
import {
  ExternalWorksBySegment,
  LinkedExternalOrder,
  LinkedWorkRef,
  RouteSegment,
} from '../../../../../types/surveillanceRoute';

// Styled
import { Container, InfoMessage } from './styles';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

interface RouteChildOrdersProps {
  orderId?: string;
  routeId: string;
  state: string;
  /** Carretera del RV — el modal de corregir la usa para las etiquetas
   *  Lleida/Girona bajo las flechas de sentido. */
  roadId?: string;
  /** TODAS las carreteras del recorrido: mc53 deja corregir a mano la del
   *  tramo, y sin la lista no hay entre qué elegir. */
  roadIds?: string[];
  /** ¿Puede este usuario añadir fotos a los tramos? Lo decide la página, que
   *  es quien tiene el recorrido entero (equipo y dueño del cronómetro). */
  canAttachPhotos?: boolean;
  /** ¿Y corregir el tramo? Mismo motivo que las fotos: desde 2026-08-15 el
   *  permiso depende del EQUIPO y del dueño del cronómetro, y aquí solo llega
   *  el estado — así que lo decide la página y la card obedece. */
  canEditSegments?: boolean;
  /** Qué versión del editor abrir; la resuelve la página con `segmentEditorMode`. */
  editorMode?: SegmentEditorMode;
  /** ¿Puede este usuario crear una básica en el recorrido y vincular
   *  actuaciones a sus tramos (2026-09-04)? Lo decide la página con
   *  `canCreateBasicActionInRoute`. Sin permiso no se pasan los callbacks de
   *  vincular y la card no pinta ni el botón «Crear +» ni la pill del tramo. */
  canCreateBasicAction?: boolean;
  /** Cross-link 3 (2026-09-22): OTs vinculadas al recorrido (del fragment de detalle). */
  linkedExternalOrders?: LinkedExternalOrder[];
  /** ¿Puede vincular/desvincular OTs? Lo decide la página (`canLinkOrdersToRoute`). */
  canLinkOrders?: boolean;
  /**
   * Lo último que trajo del servidor el loader de la pestaña: cambia en cada
   * revalidación (p. ej. la del cronómetro) y dispara el re-fetch del árbol.
   */
  serverRevision?: unknown;
}

// ═══════════════════════════════════════════════════════════════════
// COMPONENT — Uses ShowOT directly (same as Work Orders & Campaigns)
// ═══════════════════════════════════════════════════════════════════

export default function RouteChildOrders({
  orderId,
  routeId,
  state,
  roadId,
  roadIds,
  canAttachPhotos,
  canEditSegments = false,
  editorMode = 'management',
  canCreateBasicAction = false,
  linkedExternalOrders = [],
  canLinkOrders = false,
  serverRevision,
}: RouteChildOrdersProps): JSX.Element {
  const { t } = useTranslation(['pages', 'common']);
  const revalidator = useRevalidator();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    linkOrderToSegment,
    unlinkOrderFromSegment,
    correctSegment,
    updateSegmentFields,
    linkOrderToSurveillanceRoute,
    unlinkOrderFromSurveillanceRoute,
  } = useSurveillanceRoutes();
  const { currentRole } = useAuth();
  const [orderData, setOrderData] = useState<OrderDataTypes[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Trabajos que atendieron cada aviso (consulta aparte, sin caché; spec 2026-10-27).
  const [externalWorks, setExternalWorks] = useState<ExternalWorksBySegment>({});
  const openLinkedWork = useOpenLinkedWork();
  // Solo la ÚLTIMA petición del mapa puede escribir (una respuesta vieja que
  // llega tarde pisaría la nueva) y ninguna tras desmontar.
  const worksRequestId = useRef(0);
  useEffect(
    () => () => {
      worksRequestId.current = -1;
    },
    [],
  );

  // Fetch order tree using the same selector as WorkOrderDetailed.
  // Extraído a callback reutilizable (D6 2026-07-06): `revalidatorFunction`
  // de ShowOT debe poder RE-fetchear el árbol (p.ej. tras cambiar la causa
  // de un tramo) — `revalidator.revalidate()` solo re-ejecuta loaders del
  // router, no este fetch imperativo.
  const fetchTree = useCallback(
    async (silent = false) => {
      if (!orderId) return;
      if (!silent) setLoading(true);
      setError(false);
      // El mapa de trabajos va EN PARALELO y no frena el árbol: nunca rechaza y,
      // si llega tarde o vacío, la card sale como antes (solo la OT).
      const requestId = ++worksRequestId.current;
      fetchExternalWorksBySegment(routeId).then((works) => {
        // «No se sabe» (null) sale como siempre: solo la OT. Conservar lo
        // anterior exigiría comprobar que la OT de cada aviso no ha cambiado.
        if (worksRequestId.current === requestId) setExternalWorks(works ?? {});
      });
      try {
        const data = await selectWorkOrderWithChildren(true, orderId);
        if (data && Array.isArray(data)) {
          setOrderData(data as unknown as OrderDataTypes[]);
        } else {
          setError(true);
        }
      } catch {
        setError(true);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [orderId, routeId],
  );

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  // 2026-07-17: otros submits POST disparados mientras esta tab está montada
  // (p.ej. crear actuació/comunicació desde ShowOT, cambio de causa de pausa
  // desde RouteSegmentOT) navegan a rutas hermanas y vuelven aquí. Al
  // COMPLETAR esos POST (submitting/loading → idle) re-fetcheamos el árbol
  // imperativo en silencio — `revalidator.revalidate()` solo re-ejecuta
  // loaders, no este fetch (mismo motivo que refreshTree). D3 (2026-07-20):
  // la card de la propia gemela del RV YA NO submitea (read-only total, el
  // lápiz navega al wizard EditRoute) — este efecto ya no la cubre a ella,
  // pero sigue siendo necesario para el resto.
  // ── Cross-link 3: órdenes vinculadas al recorrido ──────────────────────
  const onViewLinkedOrder = useCallback(
    (link: LinkedExternalOrder) => {
      const target =
        link.order.type === OrderType.Campaign
          ? `/campaign/${link.order.id}`
          : `/work-orders/${link.order.id}`;
      navigate(target);
    },
    [navigate],
  );
  const onUnlinkOrder = useCallback(
    async (orderId: string) => {
      await unlinkOrderFromSurveillanceRoute(routeId, orderId);
      revalidator.revalidate();
    },
    [routeId, unlinkOrderFromSurveillanceRoute, revalidator],
  );
  const onLinkOrder = useCallback(() => {
    dispatch(
      openModal(modalIds.RELATE_ORDER_TO_ROUTE, {
        excludeOrderIds: linkedExternalOrders.map((l) => l.order.id),
        onConfirm: async (orderId: string) => {
          await linkOrderToSurveillanceRoute(routeId, orderId);
          revalidator.revalidate();
        },
      }),
    );
  }, [dispatch, linkedExternalOrders, linkOrderToSurveillanceRoute, routeId, revalidator]);

  // § 13.5 (2026-09-22): las OTs vinculadas se pintan como pills en la
  // cabecera de la card GEMELA del recorrido (la primera del árbol, marcada
  // por el selector con `isSurveillanceRouteOwnOrder`), con el mismo lenguaje
  // que las cards de tramo. Los handlers viven aquí (routeId, permiso y
  // revalidator) y bajan por prop; nada de secciones aparte.
  const orderDataWithLinks = useMemo(
    () =>
      orderData?.map((order) =>
        order.segmentId
          ? { ...order, linkedExternalWorks: externalWorks[order.segmentId] ?? [] }
          : order.isSurveillanceRouteOwnOrder
          ? {
              ...order,
              linkedExternalOrders,
              canLinkOrders,
              onLinkOrder,
              onUnlinkOrder,
              onViewLinkedOrder,
            }
          : order,
      ) ?? null,
    [
      orderData,
      externalWorks,
      linkedExternalOrders,
      canLinkOrders,
      onLinkOrder,
      onUnlinkOrder,
      onViewLinkedOrder,
    ],
  );
  const navigation = useNavigation();
  const prevNavState = useRef(navigation.state);
  useEffect(() => {
    if (prevNavState.current !== 'idle' && navigation.state === 'idle') {
      fetchTree(true);
    }
    prevNavState.current = navigation.state;
  }, [navigation.state, fetchTree]);

  // 🔴 2026-08-11: al drenar la cola de subida de fotos, esta pestaña se
  // quedaba con el árbol de ANTES.
  //
  // `offlineImageUploadSyncManager` sí actualiza la caché de Apollo tras cada
  // `createImage` (`Order.imageIDs` + `images`), pero aquí el árbol NO se lee
  // de la caché: es un `useState` poblado por `selectWorkOrderWithChildren`, o
  // sea una foto fija del momento en que se montó. El resto de detalles no
  // tienen este problema porque su árbol viene del loader del router y ellos
  // llaman a `useRevalidateOnSync` (`WorkOrderDetailed`, `CampaignDetailed`) —
  // pero `revalidator.revalidate()` no re-ejecuta un fetch imperativo, así que
  // aquí hay que escuchar el evento y refetchear a mano. Mismo patrón que el
  // efecto de `navigation.state` de arriba, y en silencio: el operario no debe
  // ver un «Cargando» porque una subida terminó de fondo.
  useEffect(() => {
    const onSyncComplete = () => fetchTree(true);
    window.addEventListener('offline-sync-complete', onSyncComplete);
    return () =>
      window.removeEventListener('offline-sync-complete', onSyncComplete);
  }, [fetchTree]);

  // Refresco tras un cambio (D6 2026-07-06, reutilizado por P15 Fase 1):
  // revalida los loaders del router Y re-fetchea el árbol imperativo
  // (silencioso — sin flash de "Cargando...").
  const refreshTree = useCallback(() => {
    revalidator.revalidate();
    fetchTree(true);
  }, [revalidator, fetchTree]);

  // 2026-09-24: arrancar o parar un tramo desde el cronómetro (TimerOverlay,
  // useRouteTimerActions) solo revalida los loaders del router, y este árbol
  // es un fetch imperativo: la lista se quedaba sin el tramo nuevo hasta salir
  // y volver. Cada revalidación hace que el loader de la pestaña traiga datos
  // NUEVOS del servidor (`serverRevision`); cuando cambian, se re-fetchea el
  // árbol en silencio. La primera llegada se salta: el montaje ya lo pidió.
  // (Escuchar `useRevalidator().state` no sirve: el router agrupa
  // loading → idle y el componente nunca ve el estado intermedio.)
  const lastServerRevision = useRef<unknown>(undefined);
  useEffect(() => {
    if (serverRevision == null) return;
    if (lastServerRevision.current !== undefined) fetchTree(true);
    lastServerRevision.current = serverRevision;
  }, [serverRevision, fetchTree]);

  // ── Aviso a sala desde la card del tramo (2026-09-24) ──────────────────
  // Vincular, cambiar o devolver a pendiente el aviso del tramo. La lógica
  // vive en `useSegmentAvisoLink`, compartida con la columna «Vincular» de la
  // tabla de Tramos (2026-10-08); aquí solo se adapta la fila del árbol.
  const { linkAviso, changeAviso, unlinkAviso } =
    useSegmentAvisoLink(refreshTree);
  // La card del árbol no lleva la hora del aviso: se lee de la lista de
  // tramos que ya está en la caché de Apollo. 🔴 Síncrono y sin red: el clic no
  // puede esperar a una petición que con mala cobertura ni responde ni falla
  // (el botón parecería muerto). Sin caché → `null` y la ventana usa «ahora».
  // No hace falta calentar la caché aquí: `fetchTree` → `selectWorkOrderWithChildren`
  // (gql/selectors/orders.ts, warm-up «Detalle RV») baja `GetRouteSegments` con
  // `{ surveillanceRouteId }` (= `routeId`, el id de la entidad SurveillanceRoute)
  // en cada carga del árbol, y las cards solo existen cuando ese `await` ha
  // terminado: cuando se puede pulsar «Vincular aviso», la caché ya la tiene.
  const avisoTimeOf = useCallback(
    (segmentId: string): string | null => {
      try {
        const cached = client.readQuery<{ routeSegments?: RouteSegment[] }>({
          query: GET_ROUTE_SEGMENTS,
          variables: { surveillanceRouteId: routeId },
        });
        return (
          cached?.routeSegments?.find((item) => item.id === segmentId)?.endDate ??
          null
        );
      } catch {
        return null;
      }
    },
    [client, routeId],
  );
  const onSegmentLinkExternalOrder = useCallback(
    (segment: OrderDataTypes) => {
      if (segment.segmentId)
        linkAviso(segment.segmentId, avisoTimeOf(segment.segmentId));
    },
    [linkAviso, avisoTimeOf],
  );
  const onSegmentChangeExternalOrder = useCallback(
    (segment: OrderDataTypes) => {
      if (segment.segmentId && segment.linkedOrderId)
        changeAviso(
          segment.segmentId,
          segment.linkedOrderId,
          avisoTimeOf(segment.segmentId),
        );
    },
    [changeAviso, avisoTimeOf],
  );
  const onSegmentUnlinkExternalOrder = useCallback(
    async (segment: OrderDataTypes) => {
      if (segment.segmentId && segment.linkedOrderId)
        await unlinkAviso(segment.segmentId, segment.linkedOrderId);
    },
    [unlinkAviso],
  );

  // ─── P15 Fase 1 (2026-07-08) — cross-link tram↔actuació ───
  // Ver docs/superpowers/specs/2026-07-08-p15-vincular-tram-wiring-design.md.

  // ActionOT (hija del RV) → pill "Vincular a tram" / "Vinculat amb Tr.NN" →
  // abre el modal elige-tramo ya construido (P15); el modal solo devuelve el
  // `segmentId`, la mutation la ejecutamos aquí con el hook.
  const onActionRelateToSegment = useCallback(
    (action: OrderDataTypes) => {
      dispatch(
        openModal(modalIds.RELATE_SEGMENT, {
          linkKind: 'basic-action',
          defaultSurveillanceRouteId: routeId,
          currentContextOrderId: action.id,
          onConfirm: async (segmentId: string) => {
            await linkOrderToSegment(segmentId, action.id);
            refreshTree();
          },
        }),
      );
    },
    [dispatch, routeId, linkOrderToSegment, refreshTree],
  );

  // Opción A (2026-07-10): fila "Desvincular" del menú de la pill de la
  // actuación — deshace el par y el tram recupera su CTA. El hook toastea.
  const onActionUnlinkFromSegment = useCallback(
    async (action: OrderDataTypes) => {
      if (!action.linkedSegmentEntityId) return;
      await unlinkOrderFromSegment(action.linkedSegmentEntityId, action.id);
      refreshTree();
    },
    [unlinkOrderFromSegment, refreshTree],
  );

  // RouteSegmentOT vinculado a Actuació ("Resolt per At.NN") → navega al
  // detall de l'actuació (target global, mismo que `ActionOT.handleViewDetail`
  // usa para actuacions fuera de campaña — funciona vía navegación directa
  // gracias al fallback "modo vista" de `ActionSummaryLoader` cuando no hay
  // sessionStorage previo).
  //
  // 2026-08-15: el destino pasa a ser el árbol de validación DENTRO del
  // recorrido. Aquí el id no se saca de la URL sino de la prop, que es el
  // dato que esta card ya tiene.
  const onSegmentNavigateToLinkedAction = useCallback(
    (segment: OrderDataTypes) => {
      if (segment.linkedActionId) {
        navigate(buildActionSummaryPath(segment.linkedActionId, routeId));
      }
    },
    [navigate, routeId],
  );

  // RouteSegmentOT vinculado a OT externa ("Vinculat amb OT.NN") → navega al
  // detall de la OT externa (D2). Fix ronda 1: la OT externa puede ser
  // `generic` O `campaign` (el backend permite ambas) — distinguimos el
  // target igual que ActionOT.handleViewDetail (`/work-orders/campaign/:id`
  // para campañas, `/work-orders/:id` para genéricas). Sin esto una campaña
  // vinculada aterrizaba en la ruta plana → breadcrumb con ObjectId crudo y
  // sub-nav de campaña rota.
  const onSegmentNavigateToLinkedExternal = useCallback(
    (segment: OrderDataTypes) => {
      if (!segment.linkedOrderId) return;
      if (segment.linkedOrderType === OrderType.Campaign) {
        navigate(`/work-orders/campaign/${segment.linkedOrderId}`);
      } else {
        navigate(`/work-orders/${segment.linkedOrderId}`);
      }
    },
    [navigate],
  );

  // «Resuelto por X» del aviso → abre la actuación o el accidente. La OT del
  // aviso viaja con el tramo para el contexto de vuelta del accidente.
  const onSegmentNavigateToLinkedWork = useCallback(
    (work: LinkedWorkRef, segment: OrderDataTypes) => {
      openLinkedWork(
        work,
        {
          id: segment.linkedOrderId,
          roid: segment.linkedOrderRoid,
          type: segment.linkedOrderType,
        },
        routeId,
      );
    },
    [openLinkedWork, routeId],
  );

  // ─── Corregir el tramo (2026-08-05, ampliado 2026-08-15) ───
  //
  // Mismo modal que sirve la tabla de Tramos de "Vista previa", con el MISMO
  // permiso (`canOpenSegmentEditor`) para que las dos entradas no puedan
  // divergir. Desde 2026-08-15 lo calcula la PÁGINA y baja por prop: la regla
  // ya no depende solo del rol y el estado, sino del equipo del recorrido y del
  // dueño del cronómetro, que aquí no están.

  /**
   * Los tramos se piden AQUÍ, al pulsar el lápiz, y no en el loader.
   *
   * El loader de esta pestaña se adelgazó a propósito (una sola query
   * `cache-first`) porque arrastrar el loader pesado bloqueaba la transición
   * entre pestañas. Meter `routeSegments` en él desharía justo esa mejora para
   * alimentar un botón que la mayoría de visitas no pulsa.
   *
   * Se piden todos, no solo el que se corrige, porque el modal valida el
   * solapamiento contra sus vecinos: con la lista recortada, la validación de
   * esta entrada sería MÁS LAXA que la de la tabla y dejaría pasar
   * solapamientos que el backend rechaza después.
   */
  const onSegmentEdit = useCallback(
    async (segmentOrder: OrderDataTypes) => {
      const segmentId = segmentOrder.segmentId;
      if (!segmentId) return;
      // Cinturón: este árbol viene del servidor, así que un tramo aún sin
      // sincronizar no debería aparecer aquí. Si algún día se alimentase de
      // otra fuente, la mutación viajaría con un id que el backend no conoce.
      if (hasSyntheticId({ id: segmentId })) return;

      // 🔴 El servidor primero, pero sin colgarse (spec 2026-09-11). Con una
      // raya de cobertura esta petición ni responde ni falla, y como el modal
      // se abre DESPUÉS, pulsar el lápiz no hacía absolutamente nada: ni
      // ventana, ni aviso, ni rueda. Parecía que la aplicación se había
      // colgado. Pasado el plazo se abre con los tramos cacheados; el
      // solapamiento lo sigue validando el backend al guardar.
      let data: { routeSegments?: RouteSegment[] } | null | undefined;
      try {
        ({ data } = await preferServerWithCacheFallback<{
          data?: { routeSegments?: RouteSegment[] } | null;
        }>(
          client.query({
            query: GET_ROUTE_SEGMENTS,
            variables: { surveillanceRouteId: routeId },
            fetchPolicy: 'network-only',
          }),
          () => {
            const cached = client.readQuery({
              query: GET_ROUTE_SEGMENTS,
              variables: { surveillanceRouteId: routeId },
            }) as { routeSegments?: RouteSegment[] } | null;
            return cached?.routeSegments?.length ? { data: cached } : null;
          },
        ));
      } catch (error) {
        // Ni servidor ni caché. Lo que NO vale es callarse: el operario se
        // queda pulsando un botón que no responde.
        console.error('[RV] no se pudieron cargar los tramos para corregir', error);
        showErrorToast(t('toasts:error.network'));
        return;
      }
      const segments: RouteSegment[] = data?.routeSegments ?? [];
      const segment = segments.find((item) => item.id === segmentId);
      if (!segment) {
        // 🔴 Y aquí TAMBIÉN se avisa. El respaldo puede devolver una lista
        // cacheada que sí existe pero es ANTERIOR a este tramo —creado desde
        // otra tablet, o después del último refresco con éxito—, y entonces el
        // `find` falla. Sin este aviso volveríamos al síntoma original: pulsar
        // el lápiz y que no pase absolutamente nada.
        console.warn('[RV] el tramo a corregir no está en la lista disponible', segmentId);
        showErrorToast(t('toasts:error.network'));
        return;
      }

      dispatch(
        openModal(modalIds.EDIT_SEGMENT, {
          segment,
          segments,
          roadId,
          // mc53: las del recorrido, para poder corregir la del tramo a mano.
          roadIds: roadIds ?? [],
          mode: editorMode,
          onConfirm: async (payload: EditSegmentConfirmPayload) => {
            if (editorMode === 'operator') {
              // El operario solo manda lo suyo — ver la gemela de "Vista previa".
              await updateSegmentFields({
                id: payload.id,
                direction: payload.direction,
                lane: payload.lane,
                pauseCauseDetail: payload.pauseCauseDetail ?? '',
              });
            } else {
              // `orderId` es la gemela del recorrido: se relee su fecha final.
              await correctSegment(payload, orderId);
            }
            // El árbol de hijas es un SNAPSHOT: sin refrescarlo, la card
            // seguiría enseñando los PK viejos aunque la caché ya tenga los
            // nuevos (mismo motivo que el revalidate de la otra entrada).
            refreshTree();
          },
        }),
      );
    },
    [
      correctSegment,
      dispatch,
      editorMode,
      orderId,
      refreshTree,
      roadId,
      roadIds,
      routeId,
      updateSegmentFields,
    ],
  );

  // ─── Empty / Loading / Error states ───
  if (!orderId) {
    return (
      <Container>
        <InfoMessage>
          {t('pages:surveillance.childOrders.noOrder')}
        </InfoMessage>
      </Container>
    );
  }

  if (loading && !orderData) {
    // Fetch genuinamente inevitable en la primera visita (el árbol de
    // Order + hijos no lo trae el loader del tab, ver ChildOrdersPage/
    // loader.ts). Spinner canónico (mismo que <Fallback>) en vez del
    // texto bloqueante "Cargando..." — feedback usuario 2026-07-07: se
    // percibía como un segundo estado de carga tras el cambio de tab.
    return (
      <Container>
        <Loader />
      </Container>
    );
  }

  if (error || !orderData || orderData.length === 0) {
    return (
      <Container>
        <InfoMessage>{t('common:messages.noData')}</InfoMessage>
      </Container>
    );
  }

  // Patrón WO (feedback usuario 2026-07-03): ShowOT se renderiza a pelo, sin
  // wrapper con padding — igual que WorkOrderDetailed/CampaignDetailed. El
  // Container con padding queda solo para los estados de mensaje.
  return (
    <>
    <ShowOT
      data={orderDataWithLinks ?? orderData}
      revalidatorFunction={refreshTree}
      // Fix 2026-07-08: este componente monta ShowOT bajo la ruta hija
      // `child-orders` (no la ruta index del detalle) — el default
      // `./multimedia` de ShowOT resolvía a
      // `.../child-orders/multimedia` (404). La ruta real es hermana de
      // `child-orders`, un nivel arriba.
      multimediaPath="../multimedia"
      // P15 Fase 1 (2026-07-08) — cross-link tram↔actuació. Desde 2026-09-04
      // solo si el usuario OPERA el recorrido: sin callback la card no pinta
      // la pill de vincular ni la fila de desvincular.
      onActionRelateToSegment={
        canCreateBasicAction ? onActionRelateToSegment : undefined
      }
      onActionUnlinkFromSegment={
        canCreateBasicAction ? onActionUnlinkFromSegment : undefined
      }
      onSegmentNavigateToLinkedAction={onSegmentNavigateToLinkedAction}
      onSegmentNavigateToLinkedExternal={onSegmentNavigateToLinkedExternal}
      onSegmentNavigateToLinkedWork={onSegmentNavigateToLinkedWork}
      onSegmentLinkExternalOrder={
        canLinkOrders ? onSegmentLinkExternalOrder : undefined
      }
      onSegmentChangeExternalOrder={
        canLinkOrders ? onSegmentChangeExternalOrder : undefined
      }
      onSegmentUnlinkExternalOrder={
        canLinkOrders ? onSegmentUnlinkExternalOrder : undefined
      }
      // Corregir el tramo (2026-08-05): sin permiso no se pasa el callback, y
      // sin callback la card no pinta el lápiz.
      onSegmentEdit={canEditSegments ? onSegmentEdit : undefined}
      // Fotos del tramo: el permiso lo decide la página (tiene el recorrido
      // entero); la card solo lo obedece, como con el lápiz.
      canAttachPhotos={canAttachPhotos}
      // Crear básica y vincular a tramo: mismo reparto que las fotos y el
      // lápiz — lo decide la página, la card obedece.
      canCreateBasicAction={canCreateBasicAction}
      // Cambiar la causa del tramo: gestión y recorrido sin cerrar (I1).
      canChangeSegmentCause={canChangeSegmentCauseInRoute(currentRole, state)}
    />
    </>
  );
}
